import { shapeAnalyticsProps, type BusinessShape } from "@teamna/shared";
import {
  checkAnalyticsProps,
  type AnalyticsEventFor,
  type AnalyticsProps,
  type AnalyticsRole,
} from "@teamna/shared/analytics";
import { pageMeta } from "../lib/page-meta.ts";

/**
 * Product analytics from Desk web to Mixpanel.
 *
 * Spec: ai-personal-assistant `teamna/strategy/analytics-spec.md`. Skill: `.claude/skills/teamna-analytics`.
 * Event names and properties come from `@teamna/shared/analytics`; TypeScript rejects anything else,
 * and only events whose `sources` include `desk_web` compile here.
 *
 * The token comes from the page, not from the build: the Desk API puts
 * `<meta name="teamna-mixpanel-token">` into `index.html` when `MIXPANEL_TOKEN` is set (Doppler `prd`
 * and `dev_cloud`). `vite dev` and a local API without a token have no meta, so events go to the
 * console and nothing leaves the browser. The SDK is a separate chunk that loads only with a token.
 *
 * Session replay records Desk sessions with all text, all inputs and every image masked or
 * blocked before anything leaves the browser. Images are blocked because profile photos show children.
 * It starts off and runs only on routes that `replayAllowedOn` accepts: never on `/join` or `/pay`,
 * where the URL carries an invite code or a payment token, and never on `/sign-in`, whose link can
 * carry a mobile number. `setReplayRoute` is called on every route change.
 *
 * One exception: in the team's place of a Teamna pilot business (`MyBusiness.pilotReplay`), the
 * text of the screens is shown, so we can see how the pilot uses the product. Inputs stay masked and
 * images stay blocked. A change between the two starts a new recording with the new settings.
 */

type MixpanelClient = {
  track: (event: string, properties: Record<string, unknown>) => void;
  identify: (id: string) => void;
  reset: () => void;
  register: (properties: Record<string, unknown>, options?: { persistent?: boolean }) => void;
  people: { set: (properties: Record<string, unknown>) => void };
  start_session_recording: () => unknown;
  stop_session_recording: () => unknown;
  get_session_recording_properties?: () => { $mp_replay_id?: string };
  set_config: (config: Record<string, unknown>) => void;
};

export type AnalyticsEnv = "production" | "staging" | "local";

export type InitAnalyticsOptions = {
  /** Defaults to the `teamna-mixpanel-token` meta. Null or empty means console mode. */
  token?: string | null;
  /** Defaults to the `teamna-app-env` meta, else `local`. */
  appEnv?: AnalyticsEnv;
  appVersion?: string;
  /** Test seam: the SDK loader. */
  load?: (token: string) => Promise<MixpanelClient>;
};

type Mode = "idle" | "console" | "mixpanel";

let mode: Mode = "idle";
let client: MixpanelClient | null = null;
let pending: Array<(c: MixpanelClient) => void> = [];
let baseProps: Record<string, unknown> = {};
let placeProps: Record<string, unknown> = {};
let identified: string | null = null;
let lastScreen: string | null = null;
type Recording = "off" | "masked" | "text";

/** The recording the page wants now. */
let recording: Recording = "off";
/** The recording the SDK runs, or is starting. `syncReplay` moves it to `recording`. */
let sdkRecording: Recording = "off";
let syncing = false;
/** A sign-out that `syncReplay` has not done yet: Mixpanel `reset()` after the stop. */
let resetPending = false;
/** The replay id of the recording that our last start made. A different id later means the SDK restarted it. */
let startedReplayId: string | undefined;
let replayText = false;
let replayPath: string | null = null;
const local: Array<{ event: string; properties: Record<string, unknown> }> = [];

/**
 * The SDK settings. Exported for the test that pins the privacy-relevant ones.
 *
 * Referrer and campaign capture is off: the SDK would store `document.referrer` (often the public
 * `teamna.app/join/<code>` link) and UTM values, then `people.set_once` them on the profile at
 * identify. Profiles carry only the keys the spec allows.
 */
export const MIXPANEL_INIT_OPTIONS = {
  persistence: "localStorage",
  // `screen_viewed` is sent from the router, with the route pattern and never the URL.
  track_pageview: false,
  autocapture: false,
  // No location from the IP address, the same as the server (`ip=0`) and the mobile app.
  ip: false,
  // The SDK adds the full URL and the referrer to every event. They can carry an invite code, a
  // sign-in token or ids; `screen_viewed` sends the route pattern instead.
  // `mp_keyword` and `$search_engine` are a search query the SDK reads from a search engine referrer.
  property_blacklist: ["$current_url", "$referrer", "$initial_referrer", "mp_keyword", "$search_engine"],
  save_referrer: false,
  store_google: false,
  stop_utm_persistence: true,
  track_marketing: false,
  // Off at start. `setReplayRoute` turns it on for an allowed route, so a page that opens on
  // `/join/<code>` never records a frame.
  record_sessions_percent: 0,
  record_mask_all_text: true,
  record_mask_text_selector: "*",
  record_mask_all_inputs: true,
  record_block_selector: "img, video, picture",
  // The console is a separate recorder plugin that `mp-block` and the masking do not cover (review F9.1).
  record_console: false,
} as const;

/**
 * The masking settings set before each recording starts (the SDK reads them at start). Exported for
 * the test that pins them. Only `text` differs: the text of the screens is shown, inputs and images are not.
 */
export const REPLAY_MASKING = {
  masked: {
    record_mask_all_text: true,
    record_mask_text_selector: "*",
    record_mask_all_inputs: true,
    record_block_selector: "img, video, picture",
  },
  // `mp-mask` and the other usual mask classes still hide an element.
  text: {
    record_mask_all_text: false,
    record_mask_text_selector: "",
    record_mask_all_inputs: true,
    record_block_selector: "img, video, picture",
  },
} as const;

async function loadMixpanel(token: string): Promise<MixpanelClient> {
  const mixpanel = (await import("mixpanel-browser")).default;
  // `track_marketing` is a runtime option the 2.83 types do not list.
  mixpanel.init(token, MIXPANEL_INIT_OPTIONS as unknown as Parameters<typeof mixpanel.init>[1]);
  // The SDK keeps the search query as a super property in localStorage even when it is blacklisted.
  mixpanel.unregister("mp_keyword");
  mixpanel.unregister("$search_engine");
  return mixpanel as unknown as MixpanelClient;
}

function run(action: (c: MixpanelClient) => void): void {
  if (mode !== "mixpanel") return;
  if (client) {
    try {
      action(client);
    } catch (error) {
      console.warn("[analytics] call failed", error);
    }
  } else {
    pending.push(action);
  }
}

const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

/**
 * The website's campaign tags from the address, for the Desk events of this visit (decision D7,
 * 2026-10-14): a person who comes from a teamna.app link with `?utm_source=...` keeps the tags on
 * `/sign-up` (the site passes the query on), so the sign-up funnel can be split by campaign.
 *
 * Only these three keys, and only a whole value of letters, digits, `_`, `-` and `.`, at most 60
 * characters (the website applies the same rule before it adds them to its links). They are
 * registered for this page load only (not stored), never set on the profile, and the referrer stays
 * off, so the privacy rules above still hold.
 */
export function campaignProps(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const props: Record<string, string> = {};
  for (const key of CAMPAIGN_KEYS) {
    // The whole value must be a short campaign token: a longer one is dropped, never cut (review F1.2).
    const value = params.get(key)?.trim().toLowerCase();
    if (value && value.length <= 60 && /^[a-z0-9_.-]+$/.test(value)) props[key] = value;
  }
  return props;
}

/** Starts analytics once per page. Safe to call again: later calls do nothing. */
export function initAnalytics(options: InitAnalyticsOptions = {}): void {
  if (mode !== "idle") return;
  setTextGate(replayText);
  const token = options.token === undefined ? pageMeta("teamna-mixpanel-token") : options.token;
  const appEnv = options.appEnv ?? ((pageMeta("teamna-app-env") as AnalyticsEnv | null) ?? "local");
  baseProps = { surface: "desk_web", app_env: appEnv, ...(options.appVersion ? { app_version: options.appVersion } : {}) };
  if (!token) {
    mode = "console";
    return;
  }
  mode = "mixpanel";
  (options.load ?? loadMixpanel)(token).then(
    (loaded) => {
      client = loaded;
      client.register({ ...baseProps, ...placeProps });
      const campaign = typeof window === "undefined" ? {} : campaignProps(window.location.search);
      if (Object.keys(campaign).length > 0) client.register(campaign, { persistent: false });
      // Queued calls first: a sign-out queued before the load resets before any recording starts.
      const queued = pending;
      pending = [];
      for (const action of queued) run(action);
      applyReplay();
      // The SDK can resume this tab's recording some time after init (a registry read). Look again
      // later, so a resumed recording on a route with no replay is stopped (review F9.1).
      if (typeof window !== "undefined") for (const ms of [1000, 5000]) window.setTimeout(applyReplay, ms);
    },
    (error: unknown) => {
      // A blocked script (an ad blocker) must not break the app: analytics simply stays off.
      console.warn("[analytics] Mixpanel did not load", error);
      mode = "console";
      pending = [];
    },
  );
}

/** Sends one Desk web event. A property set that does not match the plan is dropped, with a warning. */
export function track<E extends AnalyticsEventFor<"desk_web">>(event: E, props: AnalyticsProps<E>): void {
  const checked = checkAnalyticsProps(event, props);
  if (!checked.ok) {
    console.warn(`[analytics] ${event} dropped, properties do not match the plan: ${checked.issue}`);
    return;
  }
  // Not started: an app that never calls `initAnalytics` (Control Center reuses Desk screens) sends nothing.
  if (mode === "idle") return;
  const properties = { ...baseProps, ...placeProps, ...checked.props };
  if (mode === "mixpanel") {
    run((c) => c.track(event, checked.props));
    return;
  }
  local.push({ event, properties });
  if (local.length > 100) local.shift();
  // oxlint-disable-next-line no-console -- console mode is how a developer sees events without a token
  console.debug("[analytics]", event, properties);
}

/** `screen_viewed` with the route pattern (`/owner/members/$memberId`). The same screen twice in a row counts once. */
export function trackScreen(pattern: string): void {
  if (pattern === lastScreen) return;
  lastScreen = pattern;
  track("screen_viewed", { screen: pattern.slice(0, 120) });
}

/** After sign-in, with `users.id`. Calling it again with the same id does nothing. */
export function identifyUser(userId: string, appVersion?: string): void {
  if (identified === userId) return;
  identified = userId;
  run((c) => {
    c.identify(userId);
    c.people.set({ last_platform: "web", ...(appVersion ? { app_version: appVersion } : {}) });
  });
}

/**
 * The business, role and business shape of the place the person works in now. Added to every later
 * event, so every report can break down by `booking_for` and `class_fill` (migration 0069).
 * `replayText` is true only for the team's place of a pilot business: replay then shows the text.
 */
export function setAnalyticsPlace(place: { businessId: string; role: AnalyticsRole; shape: BusinessShape; replayText?: boolean } | null): void {
  placeProps = place ? { business_id: place.businessId, role: place.role, ...shapeAnalyticsProps(place.shape) } : {};
  run((c) => c.register(place ? placeProps : { business_id: null, role: null, booking_for: null, class_fill: null }));
  setTextGate(place?.replayText === true);
  applyReplay();
}

/** The owner changed the shape: the next events of that business carry the new one. */
export function setAnalyticsShape(businessId: string, shape: BusinessShape): void {
  if (placeProps.business_id !== businessId) return;
  placeProps = { ...placeProps, ...shapeAnalyticsProps(shape) };
  run((c) => c.register(shapeAnalyticsProps(shape)));
}

/**
 * Paths whose URL carries a secret (an invite code, a team invitation token, a payment link token,
 * a sign-in link token in the fragment) or a mobile number (`/sign-in?phone=`: the screen removes
 * it, but a recording that starts first keeps the URL, review F2.1). Replay never runs there.
 * `/pay` is served by the marketing host, but a Desk-host `/pay/<token>` falls through to the app.
 */
const NO_REPLAY_PREFIXES = ["/join", "/team-invite", "/pay", "/email-sign-in", "/demo-sign-in", "/sign-in"] as const;

/** Replay runs on every Desk route except the paths in `NO_REPLAY_PREFIXES` and below them. */
export function replayAllowedOn(pathname: string): boolean {
  return !NO_REPLAY_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** Starts or stops session replay for the route the page shows now. Call it on every route change. */
export function setReplayRoute(pathname: string): void {
  replayPath = pathname;
  // A second gate at the page (review F3.1): on a route with no replay, the SDK's own block class is
  // on <body> before the route renders, so a recording that the SDK still starts there records no
  // part of the page.
  if (typeof document !== "undefined") document.body?.classList.toggle(NO_REPLAY_CLASS, !replayAllowedOn(pathname));
  applyReplay();
}

/** Mixpanel's default `record_block_class` matches it: a blocked element and all inside it are not recorded. */
export const NO_REPLAY_CLASS = "mp-block";

/**
 * Mixpanel 2.83 masks the text under this class also with the text settings (`COMMON_MASK_CLASSES_SELECTOR`).
 * It is on <body> whenever the open place may not show text (review F4.1).
 */
export const TEXT_MASK_CLASS = "mp-mask";

/**
 * The page gate for text (review F4.1). The SDK starts a recording asynchronously, so a start with
 * the text settings can begin after the person has gone to a parent place, or after the pilot
 * ended. The class on <body> changes at once, before the next screen renders, so such a recording
 * still masks all text. Only a place that may show text takes it off.
 */
function setTextGate(on: boolean): void {
  replayText = on;
  if (typeof document !== "undefined") document.body?.classList.toggle(TEXT_MASK_CLASS, !on);
}

function applyReplay(): void {
  if (!client) return;
  if (replayPath !== null) recording = !replayAllowedOn(replayPath) ? "off" : replayText ? "text" : "masked";
  // The SDK restarts a recording itself (idle timeout, maximum length) with the masked settings it
  // has between our starts. A new replay id says so; the loop then starts text again (review F8.1).
  if (!syncing && sdkRecording !== "off" && startedReplayId !== undefined) {
    const now = sdkReplayId(client);
    if (now !== startedReplayId) sdkRecording = now ? "masked" : "off";
  }
  if (!syncing) void syncReplay(client);
}

/** How long a start may take before the SDK shows its replay id. After that the start counts as done. */
const START_WAIT_MS = 5000;

/** The replay id of the SDK's recording now, null when it records nothing, undefined when the SDK cannot say (a test fake). */
function sdkReplayId(c: MixpanelClient): string | null | undefined {
  if (!c.get_session_recording_properties) return undefined;
  return c.get_session_recording_properties().$mp_replay_id ?? null;
}

/** True when the SDK has a recording now. An SDK that cannot say counts as recording. */
function sdkRecords(c: MixpanelClient): boolean {
  return sdkReplayId(c) !== null;
}

/**
 * Brings the SDK to the wanted recording, one SDK call at a time (review F1.1, F2.1, F2.2). Mixpanel
 * 2.83 stops and starts asynchronously: a stop clears its registry later, and a start loads and
 * resumes the recorder after its promise. So a stop always ends before the next start, a start
 * counts as done only when the SDK shows a replay id (or after `START_WAIT_MS`), and each step
 * reads the wanted state again. The last change wins; no stale start runs.
 */
async function syncReplay(c: MixpanelClient): Promise<void> {
  syncing = true;
  try {
    for (;;) {
      // Read again after every await: the page can change place, route or account meanwhile.
      if (client !== c) break;
      // A recording that the SDK started itself (init resumes this tab's recording from its registry;
      // `reset()` does the same) has the masked settings. Count it, also when we want none, so the
      // steps below stop it or replace it (review F8.1, F9.1).
      if (sdkRecording === "off" && sdkReplayId(c)) sdkRecording = "masked";
      if (sdkRecording !== "off" && (resetPending || sdkRecording !== recording)) {
        sdkRecording = "off";
        startedReplayId = undefined;
        try {
          await c.stop_session_recording();
        } catch (error) {
          console.warn("[analytics] replay stop failed", error);
        }
        if (sdkReplayId(c)) {
          console.warn("[analytics] replay did not stop; the next change tries again");
          return;
        }
        continue;
      }
      // Sign-out (review F3.2): `reset()` makes its own stop and a check that can resume a recording,
      // so it runs only here, after our stop has ended.
      if (resetPending) {
        resetPending = false;
        try {
          c.reset();
          c.register(baseProps);
        } catch (error) {
          console.warn("[analytics] reset failed", error);
        }
        continue;
      }
      if (sdkRecording === recording) break;
      const target = recording;
      try {
        c.set_config(REPLAY_MASKING[target === "text" ? "text" : "masked"]);
        sdkRecording = target;
        await c.start_session_recording();
        const until = Date.now() + START_WAIT_MS;
        while (!sdkRecords(c) && Date.now() < until) await new Promise((resolve) => setTimeout(resolve, 50));
        startedReplayId = sdkReplayId(c) ?? undefined;
        // The SDK reads the masking only at a start. Back to masked at once, so a start that the SDK
        // makes itself (idle timeout, maximum length, `reset()`) is always masked.
        if (target === "text") c.set_config(REPLAY_MASKING.masked);
      } catch (error) {
        // Nothing to stop now; the next change tries again.
        console.warn("[analytics] replay start failed", error);
        sdkRecording = "off";
        startedReplayId = undefined;
        return;
      }
    }
  } finally {
    syncing = false;
  }
}

/**
 * The text setting of the place that is open now, from a new read of `/me/businesses`. A pilot that
 * ended while Desk is open goes back to masked at the next read (review F1.2).
 */
export function setReplayText(on: boolean): void {
  setTextGate(on);
  applyReplay();
}

/** At sign-out: a new anonymous id, so the next person on this browser is not merged into this one. */
export function resetAnalytics(): void {
  identified = null;
  placeProps = {};
  lastScreen = null;
  // The next person on this browser is recorded masked.
  setTextGate(false);
  if (client) {
    resetPending = true;
    applyReplay();
    return;
  }
  run((c) => {
    c.reset();
    c.register(baseProps);
  });
}

/** Test helper: the events kept in console mode since the last call. */
export function takeLocalAnalyticsEvents(): Array<{ event: string; properties: Record<string, unknown> }> {
  return local.splice(0, local.length);
}

/** Test helper: back to a fresh page. */
export function resetAnalyticsForTests(): void {
  mode = "idle";
  client = null;
  pending = [];
  baseProps = {};
  placeProps = {};
  identified = null;
  lastScreen = null;
  recording = "off";
  sdkRecording = "off";
  syncing = false;
  resetPending = false;
  startedReplayId = undefined;
  replayText = false;
  replayPath = null;
  local.length = 0;
  // A fresh page has neither gate class (review F6.1).
  if (typeof document !== "undefined") document.body?.classList.remove(NO_REPLAY_CLASS, TEXT_MASK_CLASS);
}
